import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function MarketingHome() {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const painPoints = [
    {
      icon: '📊',
      title: 'Scattered Data',
      description: 'Properties in one spreadsheet, loans in another, payments somewhere else. Nothing talks to each other.'
    },
    {
      icon: '🧮',
      title: 'Manual Calculations',
      description: 'Amortization schedules, late fees, payoff quotes. All calculated by hand. Error-prone and time-consuming.'
    },
    {
      icon: '😕',
      title: 'Unprofessional Image',
      description: 'No website for customers. They call and email for everything. You look like a hobby, not a business.'
    }
  ];

  const features = [
    {
      icon: '🏞️',
      title: 'Property Management',
      description: 'List and manage your entire land inventory with photos, GPS coordinates, and detailed property info.'
    },
    {
      icon: '💰',
      title: 'Loan Management',
      description: 'Track loans, payments, and balances automatically. View amortization schedules and payment history.'
    },
    {
      icon: '👤',
      title: 'Customer Portal',
      description: 'Self-service portal where buyers can view their loans, make payments, and download documents.'
    },
    {
      icon: '💳',
      title: 'Payment Processing',
      description: 'Accept payments via Square, Stripe, or your custom processor. Automatic receipts and reminders.'
    },
    {
      icon: '🏷️',
      title: 'eBay Integration',
      description: 'Generate professional eBay listings, track auction winners, and convert them to customers.'
    },
    {
      icon: '🎨',
      title: 'White-Label',
      description: 'Your brand, your domain, your business. Customize colors, logos, and content to match your brand.'
    }
  ];

  const steps = [
    {
      step: 1,
      title: 'Sign Up',
      description: 'Create your account and choose your plan. No credit card required to start your free trial.'
    },
    {
      step: 2,
      title: 'Configure',
      description: 'Add your branding, import properties, and set your financing terms and payment options.'
    },
    {
      step: 3,
      title: 'Launch',
      description: 'Go live with your professional website. Start accepting payments and managing loans.'
    }
  ];

  const comparisonData = [
    { feature: 'Property Website', spreadsheets: false, loanTools: false, deedstack: true },
    { feature: 'Customer Portal', spreadsheets: false, loanTools: 'Limited', deedstack: true },
    { feature: 'Payment Processing', spreadsheets: false, loanTools: true, deedstack: true },
    { feature: 'White-Label Branding', spreadsheets: false, loanTools: false, deedstack: true },
    { feature: 'eBay Integration', spreadsheets: false, loanTools: false, deedstack: true }
  ];

  const pricingTiers = [
    {
      name: 'Starter',
      price: '$69',
      period: '/month',
      note: 'No setup fee',
      description: 'Perfect for getting started'
    },
    {
      name: 'Pro',
      price: '$99',
      period: '/month',
      note: '$299 setup',
      description: 'Most popular for growing businesses',
      popular: true
    },
    {
      name: 'Enterprise',
      price: '$199',
      period: '/month',
      note: '$999 setup',
      description: 'For high-volume operations'
    }
  ];

  const faqs = [
    {
      question: 'Do I need technical skills?',
      answer: 'No. DeedStack™ is designed for land investors, not developers. Point-and-click setup, no coding required.'
    },
    {
      question: 'Can I use my own domain?',
      answer: 'Yes! All paid plans include custom domain support (yourcompany.com). You can also use a free subdomain (yourcompany.deedstack.com).'
    },
    {
      question: 'What payment processors are supported?',
      answer: 'We support Square, Stripe, and custom payment processors. Accept credit cards, debit cards, and ACH transfers.'
    },
    {
      question: 'Is there a contract?',
      answer: 'No long-term contracts. Pay month-to-month and cancel anytime. Your data is always yours.'
    }
  ];

  const testimonials = [
    {
      quote: "DeedStack™ transformed our land business. We went from spreadsheets and manual tracking to a fully automated system in days.",
      author: "Sarah M.",
      title: "Land Investor, Arizona"
    },
    {
      quote: "The customer portal alone has saved us hours of phone calls and emails. Our buyers love the self-service experience.",
      author: "Mike T.",
      title: "Property Finance Co."
    },
    {
      quote: "Finally, software built specifically for land financing. The team understands our business and it shows in every feature.",
      author: "Jennifer L.",
      title: "Rural Land Sales"
    }
  ];

  const renderComparisonValue = (value) => {
    if (value === true) return <span className="comparison-yes">✓</span>;
    if (value === false) return <span className="comparison-no">✗</span>;
    return <span className="comparison-partial">{value}</span>;
  };

  return (
    <div className="marketing-home">
      {/* 1. HERO SECTION */}
      <section className="marketing-hero">
        <div className="marketing-hero-content">
          <h1 className="marketing-hero-title">
            Land Financing Software for Modern Investors
          </h1>
          <p className="marketing-hero-subtitle">
            The all-in-one platform to manage properties, loans, and payments.
            Launch your land financing business in days, not months.
          </p>
          <div className="marketing-hero-buttons">
            <Link to="/signup" className="btn btn-primary btn-large">
              Start Free Trial
            </Link>
            <a
              href="https://demo.deedstack.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary btn-large"
            >
              View Demo
            </a>
          </div>
          <p className="marketing-hero-note">
            14-day free trial. No credit card required.
          </p>
        </div>
        <div className="marketing-hero-image">
          <div className="marketing-dashboard-preview">
            <div className="preview-header">
              <span className="preview-dot red"></span>
              <span className="preview-dot yellow"></span>
              <span className="preview-dot green"></span>
              <span className="preview-url">demo.deedstack.com/admin</span>
            </div>
            <div className="preview-content">
              <div className="preview-sidebar">
                <div className="preview-logo">DS</div>
                <div className="preview-nav-item active">Dashboard</div>
                <div className="preview-nav-item">Properties</div>
                <div className="preview-nav-item">Customers</div>
                <div className="preview-nav-item">Loans</div>
                <div className="preview-nav-item">Reports</div>
              </div>
              <div className="preview-main">
                <div className="preview-stats-row">
                  <div className="preview-stat-card">
                    <div className="preview-stat-label">Revenue (MTD)</div>
                    <div className="preview-stat-value">$12,450</div>
                    <div className="preview-stat-change positive">+8.2%</div>
                  </div>
                  <div className="preview-stat-card">
                    <div className="preview-stat-label">Active Loans</div>
                    <div className="preview-stat-value">24</div>
                    <div className="preview-stat-change positive">+2</div>
                  </div>
                  <div className="preview-stat-card">
                    <div className="preview-stat-label">Properties</div>
                    <div className="preview-stat-value">18</div>
                    <div className="preview-stat-change neutral">Available</div>
                  </div>
                </div>
                <div className="preview-table">
                  <div className="preview-table-header">Recent Payments</div>
                  <div className="preview-table-row">
                    <span className="preview-name">John Smith</span>
                    <span className="preview-amount">$236.45</span>
                    <span className="preview-date">Dec 15</span>
                    <span className="preview-badge success">Paid</span>
                  </div>
                  <div className="preview-table-row">
                    <span className="preview-name">Sarah Johnson</span>
                    <span className="preview-amount">$335.28</span>
                    <span className="preview-date">Dec 12</span>
                    <span className="preview-badge success">Paid</span>
                  </div>
                  <div className="preview-table-row">
                    <span className="preview-name">Mike Wilson</span>
                    <span className="preview-amount">$331.56</span>
                    <span className="preview-date">Dec 20</span>
                    <span className="preview-badge warning">Due</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. TRUST BAR */}
      <section className="marketing-trust">
        <div className="container">
          <p className="marketing-trust-text">
            TRUSTED BY LAND INVESTORS NATIONWIDE
          </p>
          <div className="marketing-trust-logos">
            <span className="trust-stat">100+ Properties Managed</span>
            <span className="trust-divider">|</span>
            <span className="trust-stat">$500K+ Payments Processed</span>
            <span className="trust-divider">|</span>
            <span className="trust-stat">50+ Active Users</span>
          </div>
        </div>
      </section>

      {/* 3. PAIN POINTS SECTION */}
      <section className="marketing-pain-points">
        <div className="container">
          <h2 className="section-title">Still Managing Your Land Business With Spreadsheets?</h2>
          <p className="section-subtitle">
            You're not alone. Most land investors struggle with these problems.
          </p>
          <div className="pain-points-grid">
            {painPoints.map((point, index) => (
              <div key={index} className="pain-point-card">
                <div className="pain-point-icon">{point.icon}</div>
                <h3>{point.title}</h3>
                <p>{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. SOLUTION OVERVIEW */}
      <section className="marketing-features-section">
        <div className="container">
          <h2 className="section-title">One Platform. Everything You Need.</h2>
          <p className="section-subtitle">
            DeedStack™ brings your entire land financing operation into one professional platform.
            Properties, loans, payments, customers. All in&nbsp;one&nbsp;place.
          </p>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="marketing-features-cta">
            <Link to="/features" className="btn btn-secondary">
              See All Features
            </Link>
          </div>
        </div>
      </section>

      {/* 5. HOW IT WORKS */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">Up and Running in 3 Simple Steps</h2>
          <p className="section-subtitle">
            No technical skills required. Get started in minutes.
          </p>

          <div className="steps steps-three">
            {steps.map((item) => (
              <div key={item.step} className="step">
                <div className="step-number">{item.step}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. COMPARISON PREVIEW */}
      <section className="marketing-comparison-preview">
        <div className="container">
          <h2 className="section-title">More Than Just Loan Servicing</h2>
          <p className="section-subtitle">
            See how DeedStack™ compares to the alternatives
          </p>

          {/* Desktop: Table view */}
          <div className="comparison-preview-table desktop-only">
            <table>
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Spreadsheets</th>
                  <th>Loan-Only Tools</th>
                  <th className="highlight">DeedStack™</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index}>
                    <td>{row.feature}</td>
                    <td>{renderComparisonValue(row.spreadsheets)}</td>
                    <td>{renderComparisonValue(row.loanTools)}</td>
                    <td className="highlight">{renderComparisonValue(row.deedstack)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: Stacked cards view */}
          <div className="comparison-mobile-cards mobile-only">
            <div className="comparison-mobile-card">
              <h4>Spreadsheets</h4>
              <ul>
                {comparisonData.map((row, index) => (
                  <li key={index}>
                    {renderComparisonValue(row.spreadsheets)}
                    <span>{row.feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="comparison-mobile-card">
              <h4>Loan-Only Tools</h4>
              <ul>
                {comparisonData.map((row, index) => (
                  <li key={index}>
                    {renderComparisonValue(row.loanTools)}
                    <span>{row.feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="comparison-mobile-card highlight">
              <h4>DeedStack™</h4>
              <ul>
                {comparisonData.map((row, index) => (
                  <li key={index}>
                    {renderComparisonValue(row.deedstack)}
                    <span>{row.feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="comparison-preview-cta">
            <Link to="/features" className="link-arrow">
              See Full Comparison →
            </Link>
          </div>
        </div>
      </section>

      {/* 7. TESTIMONIALS */}
      <section className="marketing-testimonials">
        <div className="container">
          <h2 className="section-title">What Our Customers Say</h2>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-quote">"{testimonial.quote}"</div>
                <div className="testimonial-author">
                  <strong>{testimonial.author}</strong>
                  <span>{testimonial.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. PRICING PREVIEW */}
      <section className="marketing-pricing-preview">
        <div className="container">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle">
            No hidden fees. No surprises. Start free, upgrade when you're ready.
          </p>

          <div className="pricing-preview-grid">
            {pricingTiers.map((tier, index) => (
              <div key={index} className={`pricing-preview-card ${tier.popular ? 'popular' : ''}`}>
                {tier.popular && <div className="popular-badge">Most Popular</div>}
                <h3>{tier.name}</h3>
                <div className="pricing-preview-price">
                  <span className="price">{tier.price}</span>
                  <span className="period">{tier.period}</span>
                </div>
                <div className="pricing-preview-note">{tier.note}</div>
                <p>{tier.description}</p>
              </div>
            ))}
          </div>

          <div className="pricing-preview-cta">
            <Link to="/pricing" className="link-arrow">
              View Full Pricing →
            </Link>
          </div>
        </div>
      </section>

      {/* 9. FAQ PREVIEW */}
      <section className="marketing-faq-preview">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>

          <div className="faq-preview-list">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-toggle">{expandedFaq === index ? '−' : '+'}</span>
                </button>
                {expandedFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section className="cta marketing-cta marketing-final-cta">
        <div className="cta-content">
          <h2>Ready to Professionalize Your Land Business?</h2>
          <p className="cta-subtitle">
            See DeedStack™ in action with our live demo, or start your free trial today.
          </p>

          <div className="demo-credentials">
            <p className="demo-label">Try our demo:</p>
            <div className="demo-info">
              <div className="demo-item">
                <span className="demo-icon">🌐</span>
                <a href="https://demo.deedstack.com/admin" target="_blank" rel="noopener noreferrer">
                  demo.deedstack.com/admin
                </a>
              </div>
              <div className="demo-item">
                <span className="demo-icon">📧</span>
                <span>demo@deedstack.com</span>
              </div>
              <div className="demo-item">
                <span className="demo-icon">🔑</span>
                <span>demodemo123</span>
              </div>
            </div>
          </div>

          <div className="marketing-cta-buttons">
            <Link to="/signup" className="btn btn-large btn-cta-primary">
              Start Free Trial
            </Link>
            <a
              href="https://demo.deedstack.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-large btn-cta-secondary"
            >
              View Live Demo
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default MarketingHome;
